import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";

export default function SouthIndian2() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "2");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="ow2Jm00HZe8"
        title="Thenmadurai Vaigai Nadhi — Ilaiyaraaja · Dharmathin Thalaivan (1988)"
        caption="Listen to the first 10 seconds on repeat — at least three times — before you read any further."
      />

      <SISCard label="The big idea" title="Ten seconds that contain a masterclass">
        <p>
          Most songs change chords every bar. Some change every half-bar. Ilaiyaraaja's piano
          intro to Thenmadurai Vaigai Nadhi changes chords <strong>every single beat</strong>. In
          Jason's words: <em>"It is literally 10 seconds of absolute piano madness."</em> The
          left-hand and right-hand pianos are panned apart in the recording, so on headphones you
          can hear each hand clearly.
        </p>
        <p>
          Jason's challenge: <em>"If ever you thought C major is easy you should learn this
          song."</em> It is a genuine test of your chord-shifting speed — if that isn't crisp, this
          intro will expose it immediately.
        </p>
        <p>
          But here is the deeper point: Ilaiyaraaja is not showing off. Every one of those chords
          comes from C major — it's all white notes — and the left hand carries a clear bass line
          underneath the busy right hand. Jason calls the writing <em>"very western classical in
          nature."</em>
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Piano intro — one chord per beat · C major (from Jason's chart)"
        caption="Each beat is a new chord, and every chord is diatonic to C major. Jason's chart groups them in fours and threes: C F C Dm · Am B° Am · Am Dm Am G · F Em Dm."
        chords={[
          { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4", "E4", "G4"] },
          { chord: "F",  rn: "IV",  function: "Pre-dominant", pitches: ["F3", "A3", "C4"] },
          { chord: "C",  rn: "I",   function: "Tonic",        pitches: ["C4", "E4", "G4"] },
          { chord: "Dm", rn: "ii",  function: "Pre-dominant", pitches: ["D4", "F4", "A4"] },
          { bar: true },
          { chord: "Am", rn: "vi",  function: "Submediant",   pitches: ["A3", "C4", "E4"] },
          { chord: "B°", rn: "vii°", function: "Dominant",    pitches: ["B3", "D4", "F4"] },
          { chord: "Am", rn: "vi",  function: "Submediant",   pitches: ["A3", "C4", "E4"] },
          { bar: true },
          { chord: "Am", rn: "vi",  function: "Submediant",   pitches: ["A3", "C4", "E4"] },
          { chord: "Dm", rn: "ii",  function: "Pre-dominant", pitches: ["D4", "F4", "A4"] },
          { chord: "Am", rn: "vi",  function: "Submediant",   pitches: ["A3", "C4", "E4"] },
          { chord: "G",  rn: "V",   function: "Dominant",     pitches: ["G3", "B3", "D4"] },
          { bar: true },
          { chord: "F",  rn: "IV",  function: "Pre-dominant", pitches: ["F3", "A3", "C4"] },
          { chord: "Em", rn: "iii", function: "Mediant",      pitches: ["E3", "G3", "B3"] },
          { chord: "Dm", rn: "ii",  function: "Pre-dominant", pitches: ["D3", "F3", "A3"] },
        ]}
      />

      <SISCard label="The harmonic technique" title="What makes chord-per-beat possible">
        <p>
          When chords change every bar, you have time to think ahead. When they change every beat,
          you cannot think — you must have already memorised the shape. This forces a different kind
          of learning: <strong>muscle memory over analysis</strong>. Your fingers need to know where
          the next chord lives before your brain catches up.
        </p>
        <p>
          Ilaiyaraaja achieves this density without chaos because he uses <strong>common-tone
          voice leading</strong>: many adjacent chords share one or two notes. C and F share C.
          Am and Dm share A. Am and B° share nothing — but they sit a step apart, which is the
          next smoothest connection. The arpeggiated right hand keeps the motion fluid so you
          never hear the change as a jolt.
        </p>
        <p>
          Jason's advice is to learn the left hand first: <em>"If you get the bass you'll get
          the song eventually."</em> The left hand runs C F C D, A B A, and so on under the
          chords, then breaks into a run of twelve triplets before the tune starts.
        </p>
      </SISCard>

      <SISCard label="The two hands" title="Scrambled arpeggios on top, twelve triplets below">
        <p>
          The right hand in this intro does not play block chords. It plays <strong>broken
          arpeggios</strong> in an unusual order. Jason names the chord tones low, middle and high:
          the patterns we usually practise go low–high–middle–high or high–low–middle–low, but
          Ilaiyaraaja's goes middle–middle–low–middle–high — and fast. The effect is a continuous
          shimmer of notes rather than punched chords.
        </p>
        <p>
          Just before the tune starts, the left hand plays a run of twelve triplet notes:
          A–D–G–C–E–F–D–G–C–F–G–B, landing on A. They are not random. The run opens with a chain
          of perfect-fourth leaps (A→D, D→G, G→C), steps up E–F, climbs in fourths again
          (D→G→C→F), and ends G–B–A. Jason calls them the most jumbled-up triplets you will meet —
          there is no straight line through them at all.
        </p>
      </SISCard>

      <SISCallout label="Practise this — slow is fast">
        Set your metronome to 50 bpm. Play only the left hand for the intro, the way Jason
        teaches it: C F C D, A B A, and onward, one note per beat, then the twelve triplets
        (A D G C E F D G C F G B, landing on A). When the bass is completely comfortable, add the
        right hand as simple block chords. Then — and only then — break the chords into
        arpeggios. Speed the metronome up by 5 bpm every time you can play cleanly for two full
        passes. Be patient.
      </SISCallout>

      <SISCard label="Carnatic context" title="Chromatic notes and raga mixture">
        <p>
          C major in Carnatic theory is <strong>Shankarabharanam</strong> (the 29th Melakarta), the
          equivalent of the Western major scale — and this intro never leaves it. Every note is a
          white key. Jason even suggests the key was chosen for the hands: <em>"No wonder it's
          composed on C major — any other scale would be absolutely impossible to play like
          this."</em>
        </p>
        <p>
          So the lesson here is not exotic notes. It is what a great arranger can do with seven
          plain ones: Western-style harmony moving one chord per beat, all inside a single raga.
        </p>
      </SISCard>

      <SISInfluence artists={["J.S. Bach (Two-Part Inventions)", "Western classical piano"]}>
        <p>
          Ilaiyaraaja trained in Western classical music: he studied with Master Dhanraj in Madras
          and earned a gold medal in classical guitar through the Trinity College of Music exams.
          You can hear that training in the independent bass and treble lines of this intro. For
          comparison, play one of Bach's Two-Part Inventions, where each hand has its own line.
        </p>
      </SISInfluence>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/02-thenmadurai-vaigai-nadhi-page1.png"
        caption="Jason's chart — the intro's chords, one per beat, in C major: C F C Dm · Am B° Am · Am Dm Am G · F Em Dm, with the right-hand notes written above each chord, the triplet groups, and a passage over a C D B C bass."
      />

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/02-thenmadurai-vaigai-nadhi-page2.png"
        caption="Page 2 — another passage: E, Bm7, Dsus2 and A, with the right-hand voicings, the rhythm and the bass notes (E B D A) written out."
      />

      <SISTakeaway>
        This lesson teaches you two things simultaneously: <strong>chord-shifting speed</strong>
        and <strong>learning from the bass up</strong>. The first is a physical skill — you must
        practise slowly until the shapes are automatic. The second is a habit — get the left hand
        secure and the busy right hand has something to sit on. Master both, and you will be able
        to write intros that sound far more sophisticated than the seven plain chords involved.
      </SISTakeaway>
    </LessonShell>
  );
}
